import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuth } from './useAuth';

interface ModuloPermisos {
  modulo_id: number;
  modulo_nombre: string;
  modulo_display: string;
  modulo_icono?: string;
  modulo_ruta?: string;
  permisos: {
    ver: boolean;
    crear: boolean;
    editar: boolean;
    eliminar: boolean;
  };
}

interface PermisosUsuario {
  usuario_id: number;
  rol_id: number;
  rol_nombre: string;
  rol_display: string;
  modulos: {
    [moduloNombre: string]: ModuloPermisos;
  };
}

interface UserPermissionsResponse {
  success: boolean;
  data: PermisosUsuario;
}

/**
 * Hook para obtener los permisos reales del usuario desde la API
 * Usa React Query para cachear y gestionar el estado de los permisos
 */
export const useUserPermissions = () => {
  const { user, isAuthenticated } = useAuth();

  return useQuery<PermisosUsuario | null>({
    queryKey: ['user-permissions', user?.id],
    queryFn: async (): Promise<PermisosUsuario | null> => {
      if (!user?.id) return null;

      try {
        const response = await api.get<UserPermissionsResponse>(
          `/roles-permisos/usuarios/${user.id}/permisos`
        );
        return response.data.data;
      } catch (error) {
        console.error('Error fetching user permissions:', error);
        return null;
      }
    },
    enabled: isAuthenticated && !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutos
    cacheTime: 1000 * 60 * 10, // 10 minutos
    retry: 2,
  });
};

export default useUserPermissions;
